<template>
  <div class="p-3">

    <!-- Create form view -->
    <div v-if="showCreateForm">
      <coe-exam-create-form
        :exam="editingExam"
        @close="closeCreateForm"
        @created="onExamCreated"
        @updated="onExamUpdated"
      />
    </div>

    <!-- Course group detail view -->
    <div v-else-if="selectedGroupId">
      <coe-course-group-detail
        :group-id="selectedGroupId"
        :exam-id="selectedExamId"
        @close="closeCourseGroupDetail"
      />
    </div>

    <!-- Listing view -->
    <div v-else class="col-8 mx-auto">

      <div v-if="errorMessage" class="text-danger small mb-2">{{ errorMessage }}</div>

      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <b-button variant="primary" size="sm" @click="openCreateForm" class="d-flex align-items-center">
          <i class="fas fa-plus mr-1" style="font-size: 12px !important;"></i> Create End Sem Exam
        </b-button>
      </div>

      <!-- Loading -->
      <div class="loading" v-if="fetchData">
        <load-data></load-data>
      </div>

      <!-- Empty state -->
      <div v-else-if="!exams.length" class="text-center text-muted py-5">
        <i class="fas fa-file-alt fa-3x mb-3 text-muted"></i>
        <p class="mb-0 font-weight-bold" style="font-size: 1rem;">No End Semester Exams Created</p>
        <p class="text-muted small mt-1">Click "Create End Sem Exam" to get started.</p>
      </div>

      <!-- Exam cards -->
      <div v-else>
        <div
          v-for="(exam, index) in exams"
          :key="exam.id"
          class="exam-card"
        >

        <!-- Title -->
          <div class="exam-title d-flex justify-content-between align-items-center">
            <div>
              <span class="exam-sl">
                # {{ (currentPage - 1) * perPage + index + 1 }}
              </span>
              {{ exam.exam_name }}
            </div>

            <div class="d-flex align-items-center" style="gap: 8px;">

              <!-- Edit Exam -->
              <b-button variant="outline-secondary" size="sm" @click="editExam(exam)">
                <i class="fas fa-pen mr-1" style="font-size: 14px !important;"></i>
                Edit
              </b-button>

              <!-- 1. View Hall Tickets -->
              <b-button variant="outline-primary" size="sm" @click="openHallTicketsModal(exam)">
                <i class="fas fa-id-card mr-1" style="font-size: 14px !important;"></i>
                View Hall Tickets
              </b-button>

              <!-- 2. Result Publish -->               
              <b-button v-if="exam.result_publication_status == '0'" variant="outline-success" size="sm"
                @click="publishResult(exam)">
                <i class="fas fa-check-circle mr-1" style="font-size: 14px !important;"></i>
                Result Publish
              </b-button>

              <template v-else>
                <!-- View Published Result -->
                <b-button variant="outline-primary" size="sm" @click="viewPublishedResult(exam)">
                  <i class="fas fa-eye mr-1" style="font-size: 14px !important;"></i>
                  View Published Result
                </b-button>

                <!-- Republish Result -->
                <b-button variant="outline-warning" size="sm" @click="republishResult(exam)">
                  <i class="fas fa-sync-alt mr-1" style="font-size: 14px !important;"></i>
                  Republish Result
                </b-button>
              </template>

            </div>
          </div>

          <!-- Meta details -->
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Exam Type</span>
              <b-badge :variant="examTypeBadgeVariant(exam.exam_type)" pill class="exam-type-badge mt-1">
                {{ examTypeLabel(exam.exam_type) }}
              </b-badge>
            </div>
            <div class="meta-item">
              <span class="meta-label">Semester</span>
              <span class="meta-value">{{ exam.class_id || '—' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Regulation</span>
              <span class="meta-value">{{ exam.scheme || '—' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Academic Year & Semester</span>
              <span class="meta-value">{{ exam.academic_year+'-'+ exam.academic_semester || '—' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Exam Month</span>
              <span class="meta-value">{{ formatExamPeriod(exam.exam_period) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Date of Exam Publish</span>
              <span class="meta-value">{{ formatExamPublishDate(exam.exam_publish_date) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Course Group</span>
              <a
                v-if="exam.course_group"
                href="#"
                class="meta-value text-info"
                @click.prevent="openCourseGroupModal(exam.id, exam.course_group.id, exam.course_group.name)"
              >{{ exam.course_group.name || '—' }}</a>
              <span v-else class="meta-value">—</span>
            </div>
            <div class="meta-item" v-if="exam.eligibility && exam.eligibility.attendance && exam.eligibility.attendance.min_attendance_enabled">
              <span class="meta-label">Min Attendance %</span>
              <span class="meta-value">{{ exam.eligibility.attendance.min_attendance_percentage }}%</span>
            </div>
            <div class="meta-item" v-if="exam.eligibility && exam.eligibility.attendance && exam.eligibility.attendance.condonation_enabled">
              <span class="meta-label">Condonation Threshold</span>
              <span class="meta-value">{{ exam.eligibility.attendance.condonation_threshold }}%</span>
            </div>
            <div class="meta-item" v-if="exam.eligibility && exam.eligibility.internal_marks && exam.eligibility.internal_marks.cutoff_enabled">
              <span class="meta-label">Internal Marks Cutoff %</span>
              <span class="meta-value">{{ exam.eligibility.internal_marks.cutoff_percentage }}%</span>
            </div>
            <div class="meta-item" v-if="exam.result_publication && exam.result_publication.final_mark_total">
              <span class="meta-label">Final Mark</span>
              <span class="meta-value">{{ exam.result_publication.final_mark_total }}</span>
            </div>
            <div class="meta-item" v-if="exam.result_publication && exam.result_publication.publication_type">
              <span class="meta-label">Mark or Grade</span>
              <span class="meta-value">{{ exam.result_publication.publication_type === 'grade' ? 'Grade' : 'Mark' }}</span>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-center mt-3" v-if="totalPages > 1">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @change="onPageChange"
            size="sm"
            pills
          ></b-pagination>
        </div>
      </div>

    </div>

    <!-- Hall tickets -->
    <b-modal id="coeHallTicketsModal" :title="hallTicketsModalTitle" hide-footer centered size="lg">
      <div v-if="hallTicketsErrorMessage" class="text-danger small mb-2">{{ hallTicketsErrorMessage }}</div>
      <div class="loading" v-if="loadingHallTickets">
        <load-data></load-data>
      </div>
      <div v-else-if="!hallTickets.length" class="text-muted small py-2">No hall tickets issued yet.</div>
      <template v-else>
        <div class="d-flex justify-content-end mb-2">
          <b-button
            variant="outline-success"
            size="sm"
            class="mr-2"
            :disabled="downloadingAllHallTickets"
            @click="downloadAllHallTickets"
          >
            <b-spinner small v-if="downloadingAllHallTickets" class="mr-1" />
            <i v-else class="fas fa-file-archive mr-1" style="font-size: 14px !important;"></i>
            {{ downloadingAllHallTickets ? 'Preparing ZIP...' : 'Download All as ZIP' }}
          </b-button>
          <b-button
            variant="outline-primary"
            size="sm"
            :disabled="mailingAllHallTickets"
            @click="mailAllHallTickets"
          >
            <b-spinner small v-if="mailingAllHallTickets" class="mr-1" />
            <i v-else class="fas fa-envelope mr-1" style="font-size: 14px !important;"></i>
            {{ mailingAllHallTickets ? 'Sending...' : 'Send Mail to All' }}
          </b-button>
        </div>
        <table class="table table-striped table-bordered">
          <thead class="thead-dark">
            <tr>
              <th style="width:110px">Roll Number</th>
              <th style="width:110px">Register No</th>
              <th>Student</th>
              <th style="width:120px">Class Code</th>
              <th>Courses</th>
              <th style="width:150px">Hall Ticket</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ticket in hallTickets" :key="ticket.institution_user_id">
              <td>{{ ticket.roll_number || 'RN' }}</td>
              <td>{{ ticket.register_no || '—' }}</td>
              <td>{{ ticket.first_name || '' }} {{ ticket.last_name || '' }}</td>
              <td>{{ ticket.class_code || '—' }}</td>
              <td>{{ ticket.courses.join(', ') }}</td>
              <td>
                <b-button
                  variant="primary"
                  size="sm"
                  :disabled="generatingHallTicketFor === ticket.institution_user_id"
                  @click="generateHallTicketForStudent(ticket)"
                >
                  <b-spinner small v-if="generatingHallTicketFor === ticket.institution_user_id" class="mr-1" />
                  {{ generatingHallTicketFor === ticket.institution_user_id ? 'Generating...' : 'Generate' }}
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </b-modal>
    <coe-published-result ref="publishedResult"></coe-published-result>
  </div>
</template>

<script>
import CoeExamCreateForm from "./CoeExamCreateForm.vue";
import CoeCourseGroupDetail from "./CoeCourseGroupDetail.vue";
import CoePublishedResult from "./CoePublishedResult.vue";

export default {
  name: "CoeExamListing",

  components: { CoeExamCreateForm, CoeCourseGroupDetail, CoePublishedResult },

  data() {
    return {
      fetchData: false,
      errorMessage: "",
      hallTicketsErrorMessage: "",
      showCreateForm: false,
      editingExam: null,
      exams: [],
      currentPage: 1,
      totalRows: 0,
      perPage: 15,
      selectedGroupId: null,
      selectedExamId: null,
      selectedGroupName: "",
      hallTicketsModalTitle: "",
      hallTicketsExamId: null,
      hallTickets: [],
      loadingHallTickets: false,
      generatingHallTicketFor: null,
      downloadingAllHallTickets: false,
      mailingAllHallTickets: false,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalRows / this.perPage);
    },
  },

  async mounted() {
    await this.fetchExams();
  },

  methods: {
    async publishResult(exam) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to publish the result for "${exam.exam_name}"?`,
        {
          title: "Publish Result",
          okTitle: "Publish",
          cancelTitle: "Cancel",
          okVariant: "success",
          centered: true,
        }
      );

      if (!confirmed) {
        return;
      }

      try {
        await this.$axios.post(
          `${this.$store.getters.getAPIKey.mainAPI}/end-sem-exams/${exam.id}/publish-result`
        );

        this.$toast.success("Result published successfully.", {
          position: "top",
          duration: 3000,
        });

        await this.fetchExams(this.currentPage);
      } catch (error) {
        this.$toast.error(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Failed to publish result.",
          {
            position: "top",
            duration: 3000,
          }
        );
      }
    },

    /**
 * Open the published result component for the selected exam.
 */
    viewPublishedResult(exam) {
      this.$refs.publishedResult.open(exam);
    },

    /**
     * Republish an already published result.
     *
     * Republish uses the same backend publishing endpoint.
     */
    async republishResult(exam) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to republish the result for "${exam.exam_name}"?`,
        {
          title: "Republish Result",
          okTitle: "Republish",
          cancelTitle: "Cancel",
          okVariant: "warning",
          centered: true,
        }
      );

      if (!confirmed) {
        return;
      }

      try {
        await this.$axios.post(
          `${this.$store.getters.getAPIKey.mainAPI}/end-sem-exams/${exam.id}/publish-result`
        );

        this.$toast.success("Result republished successfully.", {
          position: "top",
          duration: 3000,
        });

        await this.fetchExams(this.currentPage);
      } catch (error) {
        this.$toast.error(
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Failed to republish result.",
          {
            position: "top",
            duration: 3000,
          }
        );
      }
    },

    async fetchExams(page) {
      this.fetchData = true;
      this.errorMessage = "";
      const p = page || this.currentPage;
      try {
        const response = await this.$axios.get(
          `${this.$store.getters.getAPIKey.mainAPI}/end-sem-exams`,
          { params: { page: p } }
        );
        const data = response.data.data;
        this.exams = data.data || [];
        this.totalRows = data.total || 0;
        this.perPage = data.per_page || 15;
        this.currentPage = data.current_page || 1;
      } catch {
        this.errorMessage = "Failed to load end semester exams.";
      } finally {
        this.fetchData = false;
      }
    },

    async onPageChange(page) {
      this.currentPage = page;
      await this.fetchExams(page);
    },

    openCreateForm() {
      this.editingExam = null;
      this.showCreateForm = true;
    },

    closeCreateForm() {
      this.showCreateForm = false;
      this.editingExam = null;
    },

    editExam(exam) {
      this.editingExam = exam;
      this.showCreateForm = true;
    },

    async onExamCreated() {
      this.closeCreateForm();
      this.currentPage = 1;
      await this.fetchExams(1);
    },

    async onExamUpdated() {
      this.closeCreateForm();
      await this.fetchExams(this.currentPage);
    },

    openCourseGroupModal(examId, groupId, groupName) {
      this.selectedExamId = examId;
      this.selectedGroupId = groupId;
      this.selectedGroupName = groupName || "";
      this.$emit("breadcrumb-change", `Controller of Examinations › ${this.selectedGroupName}`);
    },

    closeCourseGroupDetail() {
      this.selectedGroupId = null;
      this.selectedExamId = null;
      this.$emit("breadcrumb-change", "Controller of Examinations");
      this.selectedGroupName = "";
    },

    // Called by the parent's modal header back-icon. Returns true if this
    // component navigated back a level itself (so the parent should not
    // close the whole modal), false if already at the root listing.
    goBack() {
      if (this.showCreateForm) {
        this.closeCreateForm();
        return true;
      }
      if (this.selectedGroupId) {
        this.closeCourseGroupDetail();
        return true;
      }
      return false;
    },

    async openHallTicketsModal(exam) {
      this.hallTicketsModalTitle = `${exam.exam_name} - Hall Tickets`;
      this.hallTicketsExamId = exam.id;
      this.hallTickets = [];
      this.hallTicketsErrorMessage = "";
      this.$bvModal.show("coeHallTicketsModal");

      this.loadingHallTickets = true;
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const response = await this.$axios.get(`${mainAPI}/end-sem-exams/${exam.id}/hall-tickets`);
        this.hallTickets = response.data.data || [];
      } catch {
        this.hallTicketsErrorMessage = "Failed to load hall tickets.";
      } finally {
        this.loadingHallTickets = false;
      }
    },

    async generateHallTicketForStudent(ticket) {
      this.generatingHallTicketFor = ticket.institution_user_id;
      this.hallTicketsErrorMessage = "";
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const response = await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.hallTicketsExamId}/students/${ticket.institution_user_id}/hall-ticket`,
          {
            course_ids: ticket.course_ids,
            roll_number: ticket.roll_number,
            class_code: ticket.class_code,
          },
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Hall_Ticket_${ticket.roll_number || ticket.institution_user_id}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        this.hallTicketsErrorMessage = "Failed to generate hall ticket.";
      } finally {
        this.generatingHallTicketFor = null;
      }
    },

    async downloadAllHallTickets() {
      this.downloadingAllHallTickets = true;
      this.hallTicketsErrorMessage = "";
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const response = await this.$axios.get(
          `${mainAPI}/end-sem-exams/${this.hallTicketsExamId}/hall-tickets/download-all`,
          { responseType: "blob" }
        );
        const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Hall_Tickets_Exam_${this.hallTicketsExamId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        this.hallTicketsErrorMessage = "Failed to download hall tickets.";
      } finally {
        this.downloadingAllHallTickets = false;
      }
    },

    async mailAllHallTickets() {
      this.mailingAllHallTickets = true;
      this.hallTicketsErrorMessage = "";
      try {
        const mainAPI = this.$store.getters.getAPIKey.mainAPI;
        const response = await this.$axios.post(
          `${mainAPI}/end-sem-exams/${this.hallTicketsExamId}/hall-tickets/mail-all`
        );
        const data = response.data.data || {};
        this.$toast.success(response.data.message || "Hall tickets emailed.", {
          position: "top",
          duration: 4000,
        });
        if (data.failed && data.failed.length) {
          const names = data.failed.map(f => f.name || f.institution_user_id).join(", ");
          this.hallTicketsErrorMessage = `Could not email: ${names}`;
        }
      } catch (error) {
        this.hallTicketsErrorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          "Failed to send hall tickets by email.";
      } finally {
        this.mailingAllHallTickets = false;
      }
    },

    formatExamPeriod(period) {
      if (!period) return '—';
      const months = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const [year, month] = period.split("-");
      return `${months[parseInt(month, 10) - 1]} ${year}`;
    },

    formatExamPublishDate(date) {
      if (!date) return '—';
      const [year, month, day] = date.slice(0, 10).split("-");
      return `${day}-${month}-${year}`;
    },

    examTypeLabel(type) {
      const labels = {
        REGULAR: "Regular",
        SUPPLEMENTARY: "Supplementary",
        IMPROVEMENT: "Improvement",
      };
      return labels[type] || type;
    },

    examTypeBadgeVariant(type) {
      const variants = {
        REGULAR: "success",
        SUPPLEMENTARY: "warning",
        IMPROVEMENT: "info",
      };
      return variants[type] || "secondary";
    },
  },
};
</script>

<style scoped>
.exam-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.exam-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 14px;
}

.exam-sl {
  display: inline-block;
  background: #e9ecef;
  color: #495057;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  margin-right: 8px;
  vertical-align: middle;
}

.exam-type-badge {
  font-size: 0.72rem;
  align-self: flex-start;
}

.meta-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px 24px;
  margin-top: 8px;
}

.meta-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.meta-label {
  /* !important, because App.vue caps every element at 12px the same way. */
  font-size: 14px !important;
  letter-spacing: 0.04em;
  color: #6c757d;
  font-weight: 600;
}

.meta-value {
  font-size: 0.85rem;
  color: #343a40;
  font-weight: 500;
}

/* Tablet */
@media (max-width: 768px) {
  .meta-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Mobile */
@media (max-width: 480px) {
  .meta-grid {
    grid-template-columns: 1fr;
  }
}
</style>
